import {
    Sun, Cloud, CloudRain, CloudSnow, CloudFog,
    Camera, MapPin, Users, Ghost,
    PenTool, FileDigit, Box,
    Scaling, Ruler, Sparkles, Sofa,
    Building2, Trees, LayoutGrid, Droplets, Footprints,
    Scissors, Palette, Lock, Globe, Search, Clock, Home
} from 'lucide-react';

export type ControlType = 'slider' | 'select' | 'text' | 'switch' | 'color' | 'custom' | 'segment' | 'multiselect';

export interface ToolControl {
    type: ControlType;
    key: string; // Path to state (e.g. 'lensParams.sunPosition' or generic 'sunPosition' if flattened)
    label: string;
    icon?: any;
    // Slider props
    min?: number;
    max?: number;
    step?: number;
    // Select/Segment props
    options?: { label: string; value: any; icon?: any }[];
    // Conditional rendering logic (simple equality)
    showIf?: { key: string; value: any };
    // Custom component identifier
    component?: string;
    placeholder?: string;
    description?: string;
}

export interface ToolSection {
    id: string;
    label?: string; // If present, renders a group header
    controls: ToolControl[];
}

export interface ToolConfig {
    id: string;
    label: string;
    sections: ToolSection[];
}

export const TOOLS: Record<string, ToolConfig> = {
    'render-bridge': {
        id: 'render-bridge',
        label: 'Render Bridge',
        sections: [
            {
                id: 'land-typology',
                label: 'Land Typology',
                controls: [
                    {
                        type: 'custom',
                        key: 'landTypology',
                        label: 'Land Typology',
                        component: 'LandTypology'
                    }
                ]
            },
            {
                id: 'atmosphere',
                label: 'Atmosphere',
                controls: [
                    {
                        type: 'slider',
                        key: 'renderBridgeParams.timeOfDay',
                        label: 'Time of Day',
                        min: 0,
                        max: 100,
                        description: 'Sunrise to Night flow'
                    },
                    {
                        type: 'multiselect',
                        key: 'renderBridgeParams.weather',
                        label: 'Weather',
                        options: [
                            { label: 'Clear', value: 'Clear', icon: Sun },
                            { label: 'Overcast', value: 'Overcast', icon: Cloud },
                            { label: 'Rainy', value: 'Rainy', icon: CloudRain },
                            { label: 'Snowy', value: 'Snowy', icon: CloudSnow },
                            { label: 'Foggy', value: 'Foggy', icon: CloudFog },
                        ]
                    },
                    {
                        type: 'slider',
                        key: 'renderBridgeParams.drama',
                        label: 'Drama Level',
                        min: 0,
                        max: 100
                    }
                ]
            },
            {
                id: 'material',
                label: 'Material Override',
                controls: [
                    {
                        type: 'custom',
                        key: 'materialOverride',
                        label: 'Material Override',
                        component: 'MaterialOverride'
                    }
                ]
            }
        ]
    },
    'lens': {
        id: 'lens',
        label: 'Lens',
        sections: [
            {
                id: 'camera',
                label: 'Camera',
                controls: [
                    {

                        key: 'lensParams.camera.viewpoints', // NOTE: Implementation needs to handle array vs single. 
                        // The original was multi-toggle. Let's stick to multiselect or handle array logic in 'select' type.
                        // Actually original was "Toggle View". I'll use multiselect for now.
                        label: 'Viewpoints',
                        type: 'multiselect',
                        options: [
                            { label: 'Eye Level', value: 'Eye Level' },
                            { label: 'Low Angle', value: 'Low Angle' },
                            { label: 'Drone Shot', value: 'Drone Shot' },
                            { label: 'Top Down', value: 'Top Down' }
                        ]
                    }
                ]
            },
            {
                id: 'geo',
                label: 'Geo-Grounding',
                controls: [
                    {
                        type: 'custom',
                        key: 'geoGrounding',
                        label: 'Map Location',
                        component: 'GeoGrounding' // Reusing logic but via config
                    }
                ]
            },
            {
                id: 'landscape',
                label: 'Landscape Features',
                controls: [
                    {
                        type: 'custom',
                        key: 'landscapeIndex',
                        label: 'Features Index',
                        component: 'LandscapeIndex'
                    }
                ]
            },
            {
                id: 'sun',
                label: 'Sun Study',
                controls: [
                    {
                        type: 'segment',
                        key: 'lensParams.sunPosition',
                        label: 'Solar Position',
                        options: [
                            { label: 'Sunrise', value: 'Sunrise' },
                            { label: 'Noon', value: 'Noon' },
                            { label: 'Sunset', value: 'Sunset' },
                            { label: 'Night', value: 'Night' }
                        ]
                    }
                ]
            },
            {
                id: 'entourage',
                label: 'Entourage Manager',
                controls: [
                    {
                        type: 'slider',
                        key: 'lensParams.entourage.crowdDensity',
                        label: 'Crowd Density',
                        min: 0, max: 100
                    },
                    {
                        type: 'select',
                        key: 'lensParams.entourage.demographic',
                        label: 'Demographic',
                        options: [
                            { label: 'Corporate', value: 'Corporate' },
                            { label: 'Family', value: 'Family' },
                            { label: 'Hipster', value: 'Hipster' },
                            { label: 'Students', value: 'Students' },
                            { label: 'Leisure', value: 'Leisure' }
                        ]
                    },
                    {
                        type: 'text',
                        key: 'lensParams.entourage.activity',
                        label: 'Activity',
                        placeholder: 'e.g. People drinking coffee'
                    },
                    {
                        type: 'switch',
                        key: 'lensParams.entourage.ghostMode',
                        label: 'Ghost Mode',
                        icon: Ghost
                    }
                ]
            }
        ]
    },
    'magic-touch': {
        id: 'magic-touch',
        label: 'Magic Touch',
        sections: [
            {
                id: 'input',
                label: 'Input Interpretation',
                controls: [
                    {
                        type: 'segment',
                        key: 'planToViewParams.sourceType',
                        label: 'Source Type',
                        options: [
                            { label: 'Hand Sketch', value: 'Hand Sketch', icon: PenTool },
                            { label: 'CAD Line', value: 'CAD Line', icon: FileDigit },
                            { label: 'Massing', value: 'Massing', icon: Box }
                        ]
                    },
                    {
                        type: 'slider',
                        key: 'planToViewParams.structuralAdherence',
                        label: 'Structural Adherence',
                        min: 0, max: 100,
                        description: 'Creative vs Strict'
                    }
                ]
            },
            {
                id: 'projection',
                label: 'Projection Engine',
                controls: [
                    {
                        type: 'segment', // Segment is good for 2-4 options
                        key: 'planToViewParams.outputView',
                        label: 'Output View',
                        options: [
                            { label: 'Axonometric', value: 'Axonometric' },
                            { label: 'Perspective', value: 'Perspective' },
                            { label: 'Top Down', value: 'Top Down' }
                        ]
                    },
                    // Note: Section Cut switch was in UI but not in store explicitly? 
                    // Store has `planToViewParams` which only lists sourceType, adherence, outputView, stylePreset.
                    // The switch was just a UI element in `ProjectionEngine.tsx` with no `onChange` connected to store.
                    // I will ADD it to the config but it might need a store update or be ignored.
                    {
                        type: 'switch',
                        key: 'planToViewParams.sectionCut', // New param
                        label: 'Section Cut',
                        icon: Scissors
                    }
                ]
            },
            {
                id: 'style',
                label: 'Style Matrix',
                controls: [
                    {
                        type: 'select',
                        key: 'planToViewParams.stylePreset',
                        label: 'Style Preset',
                        options: [
                            { label: 'Photoreal', value: 'Photoreal' },
                            { label: 'Watercolor', value: 'Watercolor' },
                            { label: 'Sketch', value: 'Sketch' },
                            { label: 'Blueprint', value: 'Blueprint' },
                            { label: 'Clay Render', value: 'Clay Render' }
                        ]
                    }
                    // Accent Color was also UI-only. I'll omit for now as it wasn't wired.
                ]
            }
        ]
    },
    'refiner': {
        id: 'refiner',
        label: 'Refiner',
        sections: [
            {
                id: 'context',
                label: 'Image Context',
                controls: [
                    {
                        type: 'segment',
                        key: 'refinerParams.imageMode',
                        label: 'Dimension Mode',
                        options: [
                            { label: 'Perspective View', value: 'Perspective View', icon: Scaling },
                            { label: 'Floor Plan', value: 'Floor Plan', icon: LayoutGrid }
                        ]
                    }
                ]
            },
            {
                id: 'action',
                label: 'Operation Action',
                controls: [
                    {
                        type: 'select',
                        key: 'refinerParams.action',
                        label: 'Target Action',
                        options: [
                            { label: 'Modify/Replace Object', value: 'Modify/Replace Object' },
                            { label: 'Remove Object', value: 'Remove Object' },
                            { label: 'Material Override', value: 'Material Override' }
                        ]
                    }
                ]
            },
            {
                id: 'scope',
                label: 'Modification Scope',
                controls: [
                    {
                        type: 'switch',
                        key: 'refinerParams.modifySelectionOnly',
                        label: 'Strict Mask Bounds',
                        description: 'When ON, leaves everything else physically identical.',
                        icon: Scissors
                    }
                ]
            },
            {
                id: 'material',
                label: 'Material Engine',
                controls: [
                    {
                        type: 'custom',
                        key: 'materialOverride',
                        label: 'Material Override',
                        component: 'MaterialOverride',
                        showIf: { key: 'refinerParams.action', value: 'Material Override' }
                    }
                ]
            }
        ]
    },
    'interior': {
        id: 'interior',
        label: 'Interior Designer',
        sections: [
            {
                id: 'room',
                label: 'Room Context',
                controls: [
                    {
                        type: 'select',
                        key: 'interiorParams.roomType',
                        label: 'Room Type',
                        options: [
                            { label: 'Living Room', value: 'living_room' },
                            { label: 'Bedroom', value: 'bedroom' },
                            { label: 'Kitchen & Dining', value: 'kitchen' },
                            { label: 'Office / Study', value: 'office' },
                            { label: 'Bathroom', value: 'bathroom' }
                        ]
                    }
                ]
            },
            {
                id: 'theme',
                label: 'Design Language',
                controls: [
                    {
                        type: 'segment',
                        key: 'interiorParams.theme',
                        label: 'Interior Theme',
                        options: [
                            { label: 'Classic', value: 'classic' },
                            { label: 'Modern', value: 'modern' },
                            { label: 'Traditional', value: 'traditional' }
                        ]
                    },
                    {
                        type: 'select',
                        key: 'interiorParams.paletteId',
                        label: 'Color Palette',
                        options: [
                            { label: 'Modern Neutrals', value: 'ip1' },
                            { label: 'Classic Elegance', value: 'ip2' },
                            { label: 'Coastal Breeze', value: 'ip3' },
                            { label: 'Earth & Stone', value: 'ip4' },
                            { label: 'Urban Industrial', value: 'ip5' },
                            { label: 'Scandi Minimal', value: 'ip6' }
                        ]
                    }
                ]
            },
            {
                id: 'lighting',
                label: 'Lighting & Atmosphere',
                controls: [
                    {
                        type: 'segment',
                        key: 'interiorParams.timeOfDay',
                        label: 'Time of Day',
                        options: [
                            { label: 'Day', value: 'Day' },
                            { label: 'Sunset', value: 'Sunset' },
                            { label: 'Night', value: 'Night' }
                        ]
                    }
                ]
            }
        ]
    }
};
